import { useState, useEffect } from "react";
import { useSignIn, useSignUp } from "@clerk/react";

const API_URL = process.env.REACT_APP_API_URL || "";

const STYLES = `
  .gs-page {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
    background-color: #ffffff;
    background-image: url('./dotted-earth.png');
    background-size: 96vmin 96vmin;
    background-position: center center;
    background-repeat: no-repeat;
    position: relative;
    display: flex;
    flex-direction: column;
    margin: 0; padding: 0;
  }

  /* ── Logo bar top-left ── */
  .gs-logo-bar {
    position: absolute;
    top: 32px;
    left: 40px;
    z-index: 10;
  }

  .gs-logo-img {
    height: 45px;
    width: auto;
    display: block;
  }

  /* ── Centered form area ── */
  .gs-center {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 5;
    padding: 120px 24px 20px;
  }

  /* ── White card ── */
  .gs-panel {
    background: #ffffff;
    border-radius: 18px;
    box-shadow: 0 8px 40px rgba(15,33,78,0.13), 0 1px 4px rgba(0,0,0,0.05);
    padding: 40px 44px;
    width: 100%;
    max-width: 380px;
    box-sizing: border-box;
  }

  .gs-panel-inner {
    width: 100%;
  }

  /* ── Copyright bottom ── */
  .gs-copyright {
    text-align: center;
    padding: 18px 24px;
    font-size: 13px;
    color: #94a3b8;
    position: relative;
    z-index: 5;
    font-family: 'Inter', sans-serif;
  }

  .gs-heading {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
    margin: 0 0 6px;
    font-family: 'Inter', sans-serif;
  }

  .gs-subhead {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 28px;
    line-height: 1.5;
    font-family: 'Inter', sans-serif;
  }

  .gs-form { display: flex; flex-direction: column; }

  .gs-fields { display: flex; flex-direction: column; gap: 20px; }

  .gs-field-group { display: flex; flex-direction: column; }

  .gs-label {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    display: block;
    margin-bottom: 7px;
    font-family: 'Inter', sans-serif;
  }

  .gs-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 7px;
  }
  .gs-label-row .gs-label { margin-bottom: 0; }

  .gs-forgot {
    font-size: 13px;
    font-weight: 500;
    color: #232231;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-family: 'Inter', sans-serif;
    text-decoration: none;
  }
  .gs-forgot:hover { text-decoration: underline; }

  .gs-input {
    background: #ffffff;
    color: #0f172a;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    font-family: 'Inter', sans-serif;
    box-sizing: border-box;
    width: 100%;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .gs-input:focus {
    border-color: #232231;
    box-shadow: 0 0 0 3px rgba(35,34,49,0.1);
  }
  .gs-input::placeholder { color: #94a3b8; }

  .gs-btn {
    background: #232231;
    color: #fff;
    border: none;
    border-radius: 10px;
    height: 48px;
    padding: 0 24px;
    font-weight: 600;
    font-size: 15px;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    transition: background 0.15s;
    letter-spacing: -0.1px;
  }
  .gs-btn:hover:not(:disabled) { background: #1a1926; }
  .gs-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .gs-back {
    background: none;
    border: none;
    padding: 0;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
  }
  .gs-back:hover { color: #0f172a; }

  .gs-link-row {
    margin-top: 20px;
    font-size: 14px;
    color: #64748b;
    text-align: center;
    font-family: 'Inter', sans-serif;
  }
  .gs-link-row a {
    color: #232231;
    text-decoration: none;
    font-weight: 500;
  }
  .gs-link-row a:hover { text-decoration: underline; }

  .gs-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 14px;
    color: #dc2626;
    font-size: 13px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
  }

  .gs-info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 14px;
    color: #1e40af;
    line-height: 1.5;
    font-family: 'Inter', sans-serif;
  }

  @keyframes gs-fade-up {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .gs-animate { opacity: 0; animation: gs-fade-up 0.45s ease forwards; }
  .gs-d1 { animation-delay: 0.05s; }
  .gs-d2 { animation-delay: 0.15s; }
  .gs-d3 { animation-delay: 0.25s; }

  @media (max-width: 600px) {
    .gs-logo-bar { top: 20px; left: 20px; }
    .gs-logo-img { height: 36px; }
    .gs-panel { padding: 32px 28px; border-radius: 14px; }
    .gs-center { padding-top: 100px; }
  }
`;

// Inject before first render so there is no layout shift on the logo or page layout.
if (!document.querySelector("[data-gs-login]")) {
  const _s = document.createElement("style");
  _s.setAttribute("data-gs-login", "");
  _s.textContent = STYLES;
  document.head.appendChild(_s);
}

function LoginPage() {
  const { signIn, setActive } = useSignIn();
  const { signUp, setActive: setSignUpActive } = useSignUp();

  const [email,    setEmail]    = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [step, setStep] = useState("email");

  const [password, setPassword] = useState("");

  const [signUpFirstName,  setSignUpFirstName]  = useState("");
  const [signUpLastName,   setSignUpLastName]   = useState("");
  const [signUpPassword,   setSignUpPassword]   = useState("");
  const [signUpConfirmPw,  setSignUpConfirmPw]  = useState("");

  const [verifyCode, setVerifyCode] = useState("");

  const [invitePassword,  setInvitePassword]  = useState("");
  const [inviteConfirmPw, setInviteConfirmPw] = useState("");

  const [resetCode,      setResetCode]      = useState("");
  const [resetPassword,  setResetPassword]  = useState("");
  const [resetConfirmPw, setResetConfirmPw] = useState("");

  const [mfaCode, setMfaCode] = useState("");
  const [mfaStrategy, setMfaStrategy] = useState("first"); // "first" | "second"

  useEffect(() => {
    return () => {
      try {
        const el = document.querySelector("[data-gs-login]");
        if (el) document.head.removeChild(el);
      } catch (_) {}
    };
  }, []);

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.status === "signup") {
        if (data.first_name) setSignUpFirstName(data.first_name);
        if (data.last_name)  setSignUpLastName(data.last_name);
        setStep("signup");
      } else if (data.status === "invited") {
        setStep("invited");
      } else if (data.status === "not_allowed") {
        setError("You don't have access to this platform. Contact your administrator.");
      } else {
        setStep("password");
      }
    } catch {
      setStep("password");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!signIn) { setError("Authentication not ready. Please refresh."); return; }
    setError(""); setLoading(true);
    try {
      // strategy: "password" is required in Clerk API v2026-05-12+
      const result = await signIn.create({ strategy: "password", identifier: email, password });

      // Clerk sometimes returns 422 inline (non-throwing) — surface the error
      if (result?.errors?.length) {
        const clerkErr = result.errors[0];
        setError(clerkErr.longMessage || clerkErr.message || "Incorrect email or password.");
        return;
      }

      const clerkSignIn = window.Clerk?.client?.signIn;
      const sessionId   = result?.createdSessionId
        || clerkSignIn?.createdSessionId
        || window.Clerk?.client?.lastActiveSessionId
        || window.Clerk?.client?.activeSessions?.[0]?.id;
      const status      = result?._status || result?.status || clerkSignIn?._status || clerkSignIn?.status;

      if (sessionId) {
        try { await setActive({ session: sessionId }); } catch (_) { /* already active */ }
        window.location.reload();
        return;
      }

      // No sessionId but session cookie is valid — force reload
      if (window.Clerk?.user) {
        window.location.reload();
        return;
      }

      if (status === "needs_client_trust" || status === "needs_second_factor") {
        try {
          const emailFactor = clerkSignIn?.supportedFirstFactors?.find(f => f.strategy === "email_code");
          if (emailFactor) {
            await clerkSignIn.prepareFirstFactor({ strategy: "email_code", emailAddressId: emailFactor.emailAddressId });
            setMfaStrategy("first");
          } else {
            await clerkSignIn.prepareSecondFactor({ strategy: "email_code" });
            setMfaStrategy("second");
          }
          setMfaCode("");
          setStep("mfa-code");
        } catch (mfaErr) {
          console.error("[login] prepare factor error:", mfaErr, JSON.stringify(mfaErr?.errors));
          setError("Sign-in requires verification but could not send a code. Please try again.");
        }
        return;
      }

      // needs_first_factor = wrong password (Clerk rejects without throwing)
      setError("Incorrect email or password.");
    } catch (err) {
      console.error("[login] signIn.create error:", err, JSON.stringify(err?.errors));
      if (window.Clerk?.user) { window.location.reload(); return; }
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || "Incorrect email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const clerkSignIn = window.Clerk?.client?.signIn;
      // prepareFirstFactor lives on window ref; attemptFirstFactor lives on hook's signIn
      // Use typeof guard to pick whichever has the method
      let result;
      if (mfaStrategy === "first") {
        const target = typeof signIn?.attemptFirstFactor === "function" ? signIn : clerkSignIn;
        result = await target.attemptFirstFactor({ strategy: "email_code", code: mfaCode });
      } else {
        const target = typeof signIn?.attemptSecondFactor === "function" ? signIn : clerkSignIn;
        result = await target.attemptSecondFactor({ strategy: "email_code", code: mfaCode });
      }
      const sessionId = result?.createdSessionId || clerkSignIn?.createdSessionId;
      if (sessionId) {
        try { await setActive({ session: sessionId }); } catch (_) { /* already active */ }
        window.location.reload();
        return;
      }
      // Clerk may auto-activate the session without exposing createdSessionId
      if (window.Clerk?.user) {
        window.location.reload();
        return;
      }
      setError("Verification failed. Please try again.");
    } catch (err) {
      console.error("[login] mfa attempt error:", err, JSON.stringify(err?.errors));
      // If session was auto-activated, treat as success
      if (window.Clerk?.user) return;
      const clerkMsg = err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message;
      setError(clerkMsg || "Incorrect code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(""); setLoading(true);
    try {
      await signIn.create({ strategy: "reset_password_email_code", identifier: email });
      setStep("reset-code");
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || "Failed to send reset email.");
    } finally { setLoading(false); }
  };

  const handleResetCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { error } = await signIn.resetPasswordEmailCode.verifyCode({ code: resetCode });
      if (error) throw error;
      setStep("reset-password");
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || err?.message || "Invalid code.");
    } finally { setLoading(false); }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    if (resetPassword !== resetConfirmPw) { setError("Passwords do not match."); return; }
    if (resetPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setLoading(true);
    try {
      const { result, error } = await signIn.resetPasswordEmailCode.submitPassword({ password: resetPassword });
      if (error) throw error;
      if (result?.createdSessionId) {
        await setActive({ session: result.createdSessionId });
      } else {
        setStep("reset-success");
        setResetCode(""); setResetPassword(""); setResetConfirmPw("");
      }
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || err?.message || "Failed to set password.");
    } finally { setLoading(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (signUpPassword !== signUpConfirmPw) { setError("Passwords do not match."); return; }
    if (signUpPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setLoading(true);
    try {
      const result = await signUp.create({
        emailAddress: email, password: signUpPassword,
        firstName: signUpFirstName.trim() || undefined,
        lastName:  signUpLastName.trim()  || undefined,
      });
      if (result.status === "complete") { await setSignUpActive({ session: result.createdSessionId }); return; }
      if (signUp.unverifiedFields?.includes("email_address")) {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setStep("verify"); return;
      }
      setError("Could not complete sign-up. Please try again.");
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || "Failed to create account.");
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code: verifyCode });
      if (result.status === "complete") {
        await setSignUpActive({ session: result.createdSessionId });
      } else { setError("Verification failed. Please try again."); }
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || "Invalid verification code.");
    } finally { setLoading(false); }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (invitePassword !== inviteConfirmPw) { setError("Passwords do not match."); return; }
    if (invitePassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!signUp?.update) { setError("Sign-up session expired. Use your invitation link again."); return; }
    setError(""); setLoading(true);
    try {
      const result = await signUp.update({ password: invitePassword });
      if (result.status === "complete") {
        await setSignUpActive({ session: result.createdSessionId });
      } else { setError("Could not complete sign-up."); }
    } catch (err) {
      setError(err?.errors?.[0]?.longMessage || "Failed to set password.");
    } finally { setLoading(false); }
  };

  const formOnSubmit =
    step === "reset-code"     ? handleResetCode :
    step === "reset-password" ? handleSetNewPassword :
    step === "set-password"   ? handleSetPassword :
    step === "signup"         ? handleSignUp :
    step === "verify"         ? handleVerify :
    step === "mfa-code"       ? handleMfaSubmit :
    step === "password"       ? handlePasswordSubmit :
                                handleEmailContinue;

  const stepHeading = {
    email:           "Welcome back",
    password:        "Welcome back",
    signup:          "Create account",
    invited:         "Check your email",
    verify:          "Check your email",
    "mfa-code":      "Check your email",
    "set-password":  "Set a password",
    "reset-code":    "Check your email",
    "reset-password":"Set new password",
    "reset-success": "Password updated",
  };

  const stepSub = {
    email:           "Please enter your details to sign in.",
    password:        `Signing in as ${email}`,
    signup:          "Set up your Geospekter access.",
    invited:         `An invitation has been sent to ${email}`,
    verify:          `We sent a 6-digit code to ${email}`,
    "mfa-code":      `We sent a verification code to ${email}`,
    "set-password":  "Complete your account setup.",
    "reset-code":    `We sent a reset code to ${email}`,
    "reset-password":"Enter your new password below.",
    "reset-success": "You can now sign in with your new password.",
  };

  return (
    <div className="gs-page">

      {/* Logo top-left */}
      <div className="gs-logo-bar">
        <img src="./logo-blue.png" alt="GeoSpekter" className="gs-logo-img" />
      </div>

      {/* Centered card */}
      <div className="gs-center">
      <div className="gs-panel">
        <div className="gs-panel-inner">

          {/* Heading */}
          <div className="gs-animate gs-d1">
            <h1 className="gs-heading">{stepHeading[step] || "Sign in"}</h1>
            <p className="gs-subhead">{stepSub[step]}</p>
          </div>

          <form className="gs-form" onSubmit={formOnSubmit}>
            <div className="gs-fields gs-animate gs-d2">

              {step === "set-password" && (
                <>
                  <div className="gs-field-group">
                    <label className="gs-label">Password</label>
                    <input type="password" minLength="8" required autoFocus className="gs-input"
                      value={invitePassword} onChange={e => setInvitePassword(e.target.value)} autoComplete="new-password" />
                    <span style={{ fontSize: 12, color: "#94a3b8", marginTop: 5 }}>
                      At least 8 characters
                    </span>
                  </div>
                  <div className="gs-field-group">
                    <label className="gs-label">Confirm password</label>
                    <input type="password" minLength="8" required className="gs-input"
                      value={inviteConfirmPw} onChange={e => setInviteConfirmPw(e.target.value)} autoComplete="new-password" />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Please wait…" : "Create account"}
                  </button>
                </>
              )}

              {step === "verify" && (
                <>
                  <div className="gs-field-group">
                    <label className="gs-label">Verification code</label>
                    <input type="text" inputMode="numeric" maxLength="6" required autoFocus
                      className="gs-input" value={verifyCode}
                      onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456" style={{ letterSpacing: "0.2em", textAlign: "center" }} />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Verifying…" : "Verify email"}
                  </button>
                </>
              )}

              {step === "mfa-code" && (
                <>
                  <div className="gs-field-group">
                    <label className="gs-label">Verification code</label>
                    <input type="text" inputMode="numeric" maxLength="8" required autoFocus
                      className="gs-input" value={mfaCode}
                      onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456" style={{ letterSpacing: "0.2em", textAlign: "center" }} />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Verifying…" : "Verify"}
                  </button>
                  <button type="button" className="gs-back"
                    onClick={() => { setStep("password"); setError(""); setMfaCode(""); }}>
                    ← Back to sign in
                  </button>
                </>
              )}

              {step === "invited" && (
                <>
                  <div className="gs-info-box">
                    Please click the invitation link sent to <strong>{email}</strong> to create your account. Check your spam folder if you don't see it.
                  </div>
                  <button type="button" className="gs-back" onClick={() => { setStep("email"); setError(""); }}>
                    ← Back
                  </button>
                </>
              )}

              {step === "signup" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="gs-field-group">
                      <label className="gs-label">First name</label>
                      <input type="text" required autoFocus className="gs-input"
                        value={signUpFirstName} onChange={e => setSignUpFirstName(e.target.value)} />
                    </div>
                    <div className="gs-field-group">
                      <label className="gs-label">Last name</label>
                      <input type="text" required className="gs-input"
                        value={signUpLastName} onChange={e => setSignUpLastName(e.target.value)} />
                    </div>
                  </div>
                  <div className="gs-field-group">
                    <label className="gs-label">Password</label>
                    <input type="password" required minLength="8" className="gs-input"
                      value={signUpPassword} onChange={e => setSignUpPassword(e.target.value)}
                      autoComplete="new-password" />
                    <span style={{ fontSize: 12, color: "#94a3b8", marginTop: 5 }}>
                      At least 8 characters
                    </span>
                  </div>
                  <div className="gs-field-group">
                    <label className="gs-label">Confirm password</label>
                    <input type="password" required minLength="8" className="gs-input"
                      value={signUpConfirmPw} onChange={e => setSignUpConfirmPw(e.target.value)}
                      autoComplete="new-password" />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Creating account…" : "Create account"}
                  </button>
                  <button type="button" className="gs-back" onClick={() => { setStep("email"); setError(""); }}>
                    ← {email}
                  </button>
                </>
              )}

              {step === "email" && (
                <>
                  <div className="gs-field-group">
                    <label htmlFor="gs-email" className="gs-label">Email</label>
                    <input id="gs-email" type="email" maxLength="256" autoComplete="username"
                      required className="gs-input" value={email}
                      onChange={e => setEmail(e.target.value)} autoFocus
                      placeholder="Enter your email" />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Please wait…" : "Continue"}
                  </button>
                  <div className="gs-link-row">
                    No account?{" "}
                    <a href="mailto:admin@geospekter.com">Contact us</a>
                  </div>
                </>
              )}

              {step === "password" && (
                <>
                  <div className="gs-field-group">
                    <div className="gs-label-row">
                      <label htmlFor="gs-password" className="gs-label">Password</label>
                      <button type="button" className="gs-forgot" disabled={loading} onClick={handleForgotPassword}>
                        Forgot password?
                      </button>
                    </div>
                    <input id="gs-password" type="password" maxLength="256"
                      autoComplete="current-password" required autoFocus
                      className="gs-input" value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password" />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Please wait…" : "Sign in"}
                  </button>
                  <button type="button" className="gs-back" onClick={() => { setStep("email"); setError(""); }}>
                    ← {email}
                  </button>
                </>
              )}

              {step === "reset-code" && (
                <>
                  <div className="gs-field-group">
                    <label className="gs-label">Reset code</label>
                    <input type="text" inputMode="numeric" maxLength="8" required autoFocus
                      className="gs-input" value={resetCode}
                      onChange={e => setResetCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456" style={{ textAlign: "center", letterSpacing: "0.15em" }} />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Verifying…" : "Verify code"}
                  </button>
                  <button type="button" className="gs-back"
                    onClick={() => { setStep("password"); setError(""); setResetCode(""); }}>
                    ← Back to sign in
                  </button>
                </>
              )}

              {step === "reset-password" && (
                <>
                  <div className="gs-field-group">
                    <label className="gs-label">New password</label>
                    <input type="password" minLength="8" required autoFocus className="gs-input"
                      value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                      autoComplete="new-password" />
                  </div>
                  <div className="gs-field-group">
                    <label className="gs-label">Confirm new password</label>
                    <input type="password" minLength="8" required className="gs-input"
                      value={resetConfirmPw} onChange={e => setResetConfirmPw(e.target.value)}
                      autoComplete="new-password" />
                  </div>
                  {error && <div className="gs-error">{error}</div>}
                  <button type="submit" className="gs-btn" disabled={loading}>
                    {loading ? "Please wait…" : "Set new password"}
                  </button>
                </>
              )}

              {step === "reset-success" && (
                <>
                  <div className="gs-info-box">
                    Your password has been updated. You can now sign in with your new password.
                  </div>
                  <button type="button" className="gs-btn" onClick={() => { setStep("email"); setError(""); }}>
                    Go to sign in
                  </button>
                </>
              )}

            </div>
          </form>
        </div>
      </div>
      </div>

      {/* Copyright */}
      <div className="gs-copyright">
        © {new Date().getFullYear()} GeoSpekter. All Rights Reserved.
      </div>

    </div>
  );
}

export default LoginPage;
